// The structure tree: every block in the file, nested. Virtualized, because an
// hour-long MP3 has ~140,000 frames.
import { store, type State } from '../state';
import { hasKids, kids, type Model } from '../model';
import type { Segment } from '../parsers/types';
import { h } from './dom';
import { theme } from './theme';
import { fmtBytes } from '../parsers/bits';
import { reveal } from './anatomy';

const ROW_H = 26;

interface Row { seg: Segment; depth: number }

export class Tree {
  readonly el: HTMLElement;
  private scroller: HTMLElement;
  private inner: HTMLElement;
  private open = new WeakSet<Segment>();
  private rows: Row[] = [];
  private rendered = new Map<number, HTMLElement>();
  private countEl: HTMLElement;

  constructor(private m: Model) {
    this.inner = h('div', { class: 'tree-inner', role: 'tree' });
    this.scroller = h('div', { class: 'tree-scroll', tabindex: '0' }, this.inner);
    this.countEl = h('span', { class: 'sub' });
    this.el = h('section', { class: 'card tree' }, h('div', { class: 'card-h' }, 'Structure', this.countEl), this.scroller);
    // Open the first level of small containers by default.
    for (const s of m.top) if (hasKids(s) && Array.isArray(s.children) && s.children.length <= 40) this.open.add(s);
    this.scroller.addEventListener('scroll', () => this.paint());
    new ResizeObserver(() => this.paint()).observe(this.scroller);
    store.sub((s, p) => this.onState(s, p));
    this.rebuild();
    this.countEl.textContent = `${m.top.length} top-level blocks`;
  }

  private onState(s: State, p: State) {
    if (s.sel !== p.sel && s.sel?.kind === 'seg') this.revealSeg(s.sel.seg);
    if (s.sel !== p.sel || s.hover !== p.hover) this.repaintClasses();
  }

  private rebuild() {
    const rows: Row[] = [];
    const walk = (list: Segment[], depth: number) => {
      for (const s of list) {
        rows.push({ seg: s, depth });
        if (this.open.has(s)) walk(kids(s), depth + 1);
      }
    };
    walk(this.m.top, 0);
    this.rows = rows;
    this.inner.style.height = rows.length * ROW_H + 'px';
    this.rendered.forEach((el) => el.remove());
    this.rendered.clear();
    this.paint();
  }

  private paint() {
    const top = this.scroller.scrollTop;
    const hgt = this.scroller.clientHeight || 600;
    const i0 = Math.max(0, Math.floor(top / ROW_H) - 10);
    const i1 = Math.min(this.rows.length, Math.ceil((top + hgt) / ROW_H) + 10);
    for (const [i, el] of this.rendered) {
      if (i < i0 || i >= i1) {
        el.remove();
        this.rendered.delete(i);
      }
    }
    for (let i = i0; i < i1; i++) {
      if (this.rendered.has(i)) continue;
      const el = this.rowEl(this.rows[i], i);
      this.inner.append(el);
      this.rendered.set(i, el);
    }
    this.repaintClasses();
  }

  private rowEl(r: Row, i: number): HTMLElement {
    const s = r.seg;
    const T = theme();
    const expandable = hasKids(s);
    const tw = h('span', { class: 'tw' }, expandable ? (this.open.has(s) ? '▾' : '▸') : '');
    const el = h(
      'div',
      { class: 'row', role: 'treeitem', style: { top: i * ROW_H + 'px', paddingLeft: 6 + r.depth * 14 + 'px' }, 'aria-expanded': expandable ? String(this.open.has(s)) : undefined },
      tw,
      h('span', { class: 'sw', style: { background: T.kind[s.kind] } }),
      h('span', { class: 'nm' }, s.name),
      s.warning ? h('span', { class: 'warn-dot', title: s.warning }, '▲') : null,
      h('span', { class: 'sm' }, s.summary ?? ''),
      h('span', { class: 'sz' }, fmtBytes(s.end - s.start)),
    );
    tw.addEventListener('click', (e) => {
      e.stopPropagation();
      this.toggle(s);
    });
    el.addEventListener('click', () => {
      store.set({ sel: { kind: 'seg', seg: s } });
      reveal({ kind: 'seg', seg: s });
    });
    el.addEventListener('dblclick', () => expandable && this.toggle(s));
    el.addEventListener('pointerenter', () => store.set({ hover: { kind: 'seg', seg: s } }));
    el.addEventListener('pointerleave', () => store.s.hover?.kind === 'seg' && store.s.hover.seg === s && store.set({ hover: null }));
    (el as HTMLElement & { _seg?: Segment })._seg = s;
    return el;
  }

  private repaintClasses() {
    const sel = store.s.sel?.kind === 'seg' ? store.s.sel.seg : null;
    const hov = store.s.hover?.kind === 'seg' ? store.s.hover.seg : null;
    for (const el of this.rendered.values()) {
      const s = (el as HTMLElement & { _seg?: Segment })._seg;
      el.classList.toggle('sel', s === sel);
      el.classList.toggle('hov', s === hov);
      el.setAttribute('aria-selected', String(s === sel));
    }
  }

  toggle(s: Segment) {
    if (this.open.has(s)) this.open.delete(s);
    else this.open.add(s);
    this.rebuild();
  }

  /** Expands the ancestors of `seg` and scrolls it into view. */
  revealSeg(seg: Segment) {
    const path = this.m.pathTo(seg);
    if (!path) return;
    let changed = false;
    for (const p of path.slice(0, -1)) if (!this.open.has(p)) { this.open.add(p); changed = true; }
    if (changed) this.rebuild();
    const i = this.rows.findIndex((r) => r.seg === seg);
    if (i < 0) return;
    const y = i * ROW_H;
    const st = this.scroller.scrollTop, hgt = this.scroller.clientHeight;
    if (y < st || y + ROW_H > st + hgt) this.scroller.scrollTop = y - hgt / 3;
  }

  /** Visible row order, for keyboard stepping. */
  siblingOf(seg: Segment, dir: 1 | -1): Segment | null {
    const path = this.m.pathTo(seg);
    if (!path) return null;
    const list = path.length > 1 ? kids(path[path.length - 2]) : this.m.top;
    const i = list.indexOf(seg);
    return list[i + dir] ?? null;
  }
}
